import { getAsset } from './prepareAssets';

export function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export function readAsImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    if (src instanceof Blob) {
      const url = window.URL.createObjectURL(src);
      img.src = url;
    } else {
      img.src = src;
    }
  });
}

export function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function readAsPDF(file, flatten = true) {
  const pdfjsLib = await getAsset('pdfjsLib');
  
  // Ensure Web Workers are disabled to prevent DataCloneError
  if (pdfjsLib.GlobalWorkerOptions) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = false;
    pdfjsLib.GlobalWorkerOptions.workerPort = null;
    pdfjsLib.GlobalWorkerOptions.standardFontDataUrl = 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/';
  }
  
  // Safari possibly get webkitblobresource error 1 when using origin file blob
  const blob = new Blob([file]);
  const url = window.URL.createObjectURL(blob);
  
  try {
    const pdf = await pdfjsLib.getDocument({
      url: url,
      disableWorker: true,
      disableScripting: true,
      disableAutoFetch: false,
      disableStream: false,
      useSystemFonts: false,
      isEvalSupported: false,
      standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/'
    }).promise;
    
    if (!flatten) {
      return pdf;
    }
    
    // Flatten mode: render to canvas and create new PDF
    const PDFLib = await getAsset('PDFLib');
    
    // Render each page to canvas at high scale for quality
    const pageData = [];
    const SCALE = 3; // Higher scale = better resolution
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewportBase = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: SCALE });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      pageData.push({
        canvas,
        width: viewportBase.width,
        height: viewportBase.height
      });
    }
    
    // Create new PDF with flattened images, preserving original dimensions
    const pdfDoc = await PDFLib.PDFDocument.create();
    
    for (const { canvas, width, height } of pageData) {
      const pngImage = await pdfDoc.embedPng(canvas.toDataURL('image/png'));
      const page = pdfDoc.addPage([width, height]);
      page.drawImage(pngImage, {
        x: 0,
        y: 0,
        width: width,
        height: height
      });
    }
    
    const pdfBytes = await pdfDoc.save();
    const flattenedBlob = new Blob([pdfBytes], { type: 'application/pdf' });
    
    // Return as PDF object so it works like the normal return
    const flatUrl = window.URL.createObjectURL(flattenedBlob);
    return await pdfjsLib.getDocument({
      url: flatUrl,
      disableWorker: true,
      disableScripting: true,
      disableAutoFetch: false,
      disableStream: false,
      useSystemFonts: false,
      isEvalSupported: false,
      standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/'
    }).promise;
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
